import React, { useContext, useState, useRef } from "react";
import { fabric } from "fabric";
import { v1 as uuid } from "uuid";
import { emitAdd, emitClearCanvas, emitImageData, emitText, emitBackgroundColorChange, onBackgroundColorChange, emitCanvasData, emitDeleteObject, emitClonedObj, emitShapeColorChange, emitSendToBack, emitBringToFront, emitObjID, emitStrokeColorChange, emitUnGroup } from "../socket";
import "fabric-history";
import { useParams } from "react-router";
import { ContactsOutlined, CopyOutlined } from "@ant-design/icons";
import bringToFrontImg from '../assets/bringToFron.png'
import SendtoBackImg from '../assets/sendToback.png'
import deleteIcon from '../assets/DeleteButton.jpg'
import copyIcon from '../assets/copyIcon-2.png'
import groupIcon from '../assets/groupIcon.png'
import ungroup from '../assets/ungroup.png'
import ungroupWhite from '../assets/ungroup-white.png'
import SendBackWhite from '../assets/sendBack-white.png'
import groupWhite from '../assets/group-white.png'
import duplicateWhite from '../assets/duplicate-white.png'
import deleteWhite from '../assets/delete-white.png'
import bringFrontWhite from '../assets/bringFront-white.png';
import presentModeIcon from '../assets/play-button-arrowhead.png';
import {
  enable as enableDarkMode,
  disable as disableDarkMode,
} from 'darkreader';
import { boardServices } from "../api";
import { centerObjectInViewport, getMaxRightOfElement, isOffline } from "../utils/helper";
import { calculatePolygonPoints } from "../utils/canvasInit";


const CanvasContext = React.createContext();

export const CanvasProvider = ({ children }) => {
  const isMobile = window.screen.width < 768
  const [isSnapToGrid, setSnapToGrid] = useState(false);
  const [isChangeSomething, setIsChangeSomething] = useState(false);
  const lastChangeRef = useRef(null);
  let undoStack = [];
  let redoStack = [];
  let reloadStack = [];
  const canvasRef = useRef(null);
  let draw = false,
    x1,
    readOnly,
    y1,
    x2,
    y2,
    pausePanning,
    zoomStartScale,
    currentX,
    currentY,
    xChange,
    lastX,
    yChange,
    lastY;

  // let SCALE_FACTOR = 1.3; //on clicking zoom, enlarge canvas in 30%. TBD
  // let zoomMax = 6;   //TBD

  //Below Variable created for savind id that comes when PrepareCanvas executes (on Loading or Opening Board Document.) this Id is Document ID taken from params in component where prepareCanvas is called
  let DocID;

  const prepareCanvas = (id) => {
    // Create a new Fabric.js canvas instance
    const canvas = new fabric.Canvas("canvas", {
      fireRightClick: true,
      fireMiddleClick: true,
      stopContextMenu: true,
      allowTouchScrolling: true,
      preserveObjectStacking: true,
    });

    canvasRef.current = canvas;
    // Set default zoom level to 38%
    canvas.setZoom(0.38);

    // Background pattern variables
    let patternCanvas;
    let pattern;

    const updateBackgroundPosition = () => {
      const vpt = canvas.viewportTransform;

      if (!vpt || !patternCanvas) {
        console.error("Viewport transform or pattern canvas is not initialized.");
        return;
      }

      const offsetX = -vpt[4] % patternCanvas.width;
      const offsetY = -vpt[5] % patternCanvas.height;

      pattern.offsetX = offsetX;
      pattern.offsetY = offsetY;

      canvas.setBackgroundColor(pattern, canvas.renderAll.bind(canvas));
    };

    const createDottedBackground = () => {
      const size = 20; // Spacing between dots
      const dotSize = 2; // Dot radius

      patternCanvas = document.createElement("canvas");
      patternCanvas.width = size;
      patternCanvas.height = size;

      const ctx = patternCanvas.getContext("2d");
      ctx.fillStyle = "#e7e7e7";
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, dotSize, 0, 2 * Math.PI);
      ctx.fill();

      pattern = new fabric.Pattern({
        source: patternCanvas,
        repeat: "repeat",
      });

      canvas.setBackgroundColor(pattern, canvas.renderAll.bind(canvas));
    };

    const resizeCanvas = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      canvas.setWidth(width);
      canvas.setHeight(height);
      canvas.calcOffset();

      updateBackgroundPosition();
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    createDottedBackground();

    let isPanning = false;
    let lastX = 0;
    let lastY = 0;

    // Mouse down event for panning
    canvas.on("mouse:down", (opt) => {
      const evt = opt.e;
      const target = opt.target;
      if (target && target.id) {
        // If the clicked target is a sticky note, make it editable
        if (target.id.includes('sticky-note')) {
          const stickyNote = target;
          const textBox = stickyNote.item(1); // Get the TextBox inside the sticky note
          // Check if it's not already in editing mode
          if (!textBox.isEditing) {
            textBox.enterEditing(); // Enter editing mode
          }

          // Ensure other sticky notes exit editing mode when this one is selected
          canvas.getObjects().forEach((obj) => {
            if (obj !== stickyNote && obj.type === 'group') {
              const otherTextBox = obj.item(1);
              if (otherTextBox instanceof fabric.Textbox) {
                otherTextBox.exitEditing(); // Exit editing for other sticky notes
              }
            }
          });
          // canvas.renderAll(); // Re-render the canvas to apply changes
        }
        else if (target.id.includes('Frame')) {
          const frame = target;
          frame.sendToBack();
        }
      }
      // Handle panning: middle mouse button or Ctrl/Cmd key
      if (evt.button === 1 || evt.ctrlKey || evt.metaKey) {
        isPanning = true;
        lastX = evt.clientX;
        lastY = evt.clientY;
        canvas.selection = false; // Disable selection during panning
      }

      // Check if the click was outside the sticky note to exit editing mode
      if (!target || !target.id || !target.id.includes('sticky-note')) {
        // Exit editing mode for all sticky notes if clicking outside
        canvas.getObjects().forEach((obj) => {
          if (obj.type === 'group') {
            const textBox = obj.item(1);
            if (textBox instanceof fabric.Textbox && textBox.isEditing) {
              textBox.exitEditing(); // Exit editing mode for any sticky note
            }
          }
        });
        // canvas.renderAll(); // Re-render the canvas after exiting editing
      }
    });

    // Mouse move event for panning
    canvas.on("mouse:move", (opt) => {
      if (isPanning) {
        const e = opt.e;
        const deltaX = e.clientX - lastX;
        const deltaY = e.clientY - lastY;

        const vpt = canvas.viewportTransform;
        vpt[4] += deltaX; // Update x offset
        vpt[5] += deltaY; // Update y offset

        canvas.setViewportTransform(vpt);
        updateBackgroundPosition();

        lastX = e.clientX;
        lastY = e.clientY;
      }
    });

    // Mouse up event to end panning
    canvas.on("mouse:up", () => {
      isPanning = false;
      canvas.selection = true;
    });

    // Zoom handling with Ctrl/Cmd + mouse wheel or touchpad gesture
    canvas.on("mouse:wheel", (opt) => {
      opt.e.preventDefault();
      opt.e.stopPropagation();

      const deltaY = opt.e.deltaY;

      canvas.getObjects().forEach((obj) => {
        if (obj.type === 'group') {
          const textBox = obj.item(1);
          if (textBox instanceof fabric.Textbox && textBox.isEditing) {
            textBox.exitEditing(); // Exit editing mode for any sticky note
          }
        }
      });

      if (opt.e.ctrlKey || opt.e.metaKey) { // Check if Ctrl or Cmd key is pressed
        const zoomFactor = deltaY < 0 ? 1.1 : 0.9; // Zoom in or out based on scroll direction
        const zoom = canvas.getZoom();
        const newZoom = Math.min(Math.max(zoom * zoomFactor, 0.2), 3); // Limit zoom levels

        const point = new fabric.Point(opt.e.offsetX, opt.e.offsetY);
        canvas.zoomToPoint(point, newZoom); // Zoom at the mouse position

        updateBackgroundPosition();
      } else {
        let deltaY = opt.e.deltaY; // Vertical scroll
        let deltaX = opt.e.deltaX; // Horizontal scroll
        let zoom = canvasRef.current.getZoom(); // Current zoom level

        // Calculate the pan distance, accounting for zoom level
        let panX = -deltaX / zoom;
        let panY = -deltaY / zoom;

        // Apply both horizontal and vertical panning
        canvasRef.current.relativePan({ x: panX, y: panY });

        // Optionally, emit the canvas data
        let cvData = canvasRef.current.toJSON(['selectable', 'id', 'type']);
        !isOffline() && emitCanvasData(id, cvData);
      }
    });

    if (id) {
      const cvData = canvas.toJSON(["selectable", "id", "type"]);
      !isOffline() && emitCanvasData(id, cvData);
    }
    canvas.on("object:scaling", (opt) => {
      const target = opt.target;
      canvas.getObjects().forEach((obj) => {
        if (obj.type === 'group') {
          const textBox = obj.item(1);
          if (textBox instanceof fabric.Textbox && textBox.isEditing) {
            textBox.exitEditing();
          }
        }
      });
    });
  };

  const Panning = (roomID) => {
    let deltaX = 0;
    let deltaY = 0;
    let panning;
    let isPanning;
    canvasRef.current.on("mouse:move", function (event) {
      if (isPanning && event.e && event && panning) {
        canvasRef.current.setCursor("grab");
        canvasRef.current.renderAll();
        canvasRef.current.selection = false;
        let movementX = event.e.movementX;
        if (deltaX >= 0) {
          deltaX -= movementX;
        }
        if (deltaX < 0) {
          movementX = deltaX + movementX;
          deltaX = 0;
        }
        let movementY = event.e.movementY;
        if (deltaY >= 0) {
          deltaY -= movementY;
        }
        if (deltaY < 0) {
          movementY = deltaY + movementY;
          deltaY = 0;
        }
        if (deltaX >= 0 && deltaY >= 0) {
          canvasRef.current.relativePan(new fabric.Point(movementX, movementY));
        }
      }
    });
    canvasRef.current.on("touch:drag", function (e) {
      if (isPanning && e.e && e && panning) {
        canvasRef.current.setCursor("grab");
        canvasRef.current.renderAll();
        canvasRef.current.selection = false;
        let movementX = e.e.movementX;
        if (deltaX >= 0) {
          deltaX -= movementX;
        }
        if (deltaX < 0) {
          movementX = deltaX + movementX;
          deltaX = 0;
        }
        let movementY = e.e.movementY;
        if (deltaY >= 0) {
          deltaY -= movementY;
        }
        if (deltaY < 0) {
          movementY = deltaY + movementY;
          deltaY = 0;
        }
        if (deltaX >= 0 && deltaY >= 0) {
          canvasRef.current.relativePan(new fabric.Point(movementX, movementY));
        }
      }
    });

    function bringToFront() {
      const activeObject = canvasRef.current.getActiveObject();
      if (activeObject) {

        activeObject.bringToFront();
        let objectID = activeObject.id
        !isOffline() && emitBringToFront({ DocID, activeObject, objectID })
        canvasRef.current.renderAll();
        saveStateToUndoStack()
      }
      closeContextMenu();
    }

    function sendToBack() {
      const activeObject = canvasRef.current.getActiveObject();
      if (activeObject) {

        activeObject.sendToBack();
        let objectID = activeObject.id
        !isOffline() && emitSendToBack({ DocID, activeObject, objectID })
        canvasRef.current.renderAll();
        saveStateToUndoStack()
      }
      closeContextMenu();
    }


    // Function to close the menu pop Up On RIght Click
    function closeContextMenu() {
      // Retrieve the context menu element by its ID
      const contextMenu = document.getElementById('context-menu');

      // Check if the context menu element exists
      if (contextMenu) {
        // Remove the context menu element from its parent node
        contextMenu.parentNode.removeChild(contextMenu);
      }
    }

    // Prevent the default context menu from appearing on the canvas
    canvasRef.current.on('contextmenu', function (e) {
      e.preventDefault();
    });

    // Event handler for mouse down on the canvas
    canvasRef.current.on('mouse:down', function (opt) {
      // Get the event and prevent the default context menu
      let evt = opt.e;
      evt.preventDefault();
      const contextMenu = document.getElementById('context-menu');

      // Check if the right mouse button is clicked
      if (opt.button === 3 && !contextMenu) {
        // Display the context menu for the clicked target
        if (opt.target) {
          showContextMenu(opt.target, evt);
        }
      }

      // Check if the Alt key is pressed
      if (evt.altKey === true) {
        // Set flags and update canvas properties for panning
        panning = true;
        isPanning = true;
        canvasRef.current.setCursor('grab');
        canvasRef.current.renderAll();
        canvasRef.current.selection = false;
      }
    });


    // Function to display the context menu at a specified position
    function showContextMenu(target, event) {
      // Create a new div element for the context menu
      const contextMenu = document.createElement('div');

      // Set styling and position properties for the context menu
      contextMenu.id = 'context-menu';
      contextMenu.style.width = '150px';
      contextMenu.style.position = 'absolute';
      contextMenu.style.top = `${event.clientY}px`;
      contextMenu.style.left = `${event.clientX}px`;
      contextMenu.style.background = 'white';
      contextMenu.style.border = '1px solid #ccc';
      contextMenu.style.padding = '13px';
      contextMenu.style.boxShadow = 'rgba(0, 0, 0, 0.54) 0px 3px 8px';
      contextMenu.style.borderRadius = '5px';

      // Create options for the context menu
      const bringToFrontOption = document.createElement('div');
      // bringToFrontOption.textContent = 'Bring to Front';
      bringToFrontOption.style.cursor = 'pointer';

      bringToFrontOption.addEventListener('mouseenter', () => {
        bringToFrontOption.style.fontWeight = '600'  // Change the background color on hover
      });

      bringToFrontOption.addEventListener('mouseleave', () => {
        bringToFrontOption.style.fontWeight = ''  // Reset the background color on mouse leave
      });

      // Create an img element for the icon
      const bringToFrontIcon = document.createElement('img');
      bringToFrontIcon.style.marginRight = '8px'
      bringToFrontIcon.style.paddingRight = '5px'
      bringToFrontIcon.style.width = '20px'

      bringToFrontIcon.src = darkMode_ ? bringFrontWhite : bringToFrontImg; // Replace with the actual path to your icon image
      // sendToBackIcon.alt = 'Send to Back';

      // Create a text label for the button
      const bringToFrontText = document.createElement('span');
      bringToFrontText.textContent = 'Bring to Front';

      // Append the icon and text to the button
      bringToFrontOption.appendChild(bringToFrontIcon);
      bringToFrontOption.appendChild(bringToFrontText);

      bringToFrontOption.addEventListener('click', bringToFront);

      //Send TO Back Button 

      const sendToBackOption = document.createElement('div');
      // sendToBackOption.textContent = 'Send to Back';
      sendToBackOption.style.cursor = 'pointer';

      sendToBackOption.addEventListener('mouseenter', () => {
        sendToBackOption.style.fontWeight = '600'  // Change the background color on hover
      });

      sendToBackOption.addEventListener('mouseleave', () => {
        sendToBackOption.style.fontWeight = ''  // Reset the background color on mouse leave
      });

      // Create an img element for the icon
      const sendToBackIcon = document.createElement('img');
      sendToBackIcon.style.marginRight = '8px'
      sendToBackIcon.style.paddingRight = '5px'
      sendToBackIcon.src = darkMode_ ? SendBackWhite : SendtoBackImg; //  actual path to your icon image
      sendToBackIcon.style.width = '20px'
      // sendToBackIcon.alt = 'Send to Back';

      // Create a text label for the button
      const sendToBackText = document.createElement('span');
      sendToBackText.textContent = 'Send to Back';

      // Append the icon and text to the button
      sendToBackOption.appendChild(sendToBackIcon);
      sendToBackOption.appendChild(sendToBackText);

      sendToBackOption.addEventListener('click', sendToBack);


      //Delete Button
      const deleteOption = document.createElement('div');
      // sendToBackOption.textContent = 'Send to Back';
      deleteOption.style.cursor = 'pointer';

      deleteOption.addEventListener('mouseenter', () => {
        deleteOption.style.fontWeight = '600'  // Change the background color on hover
      });

      deleteOption.addEventListener('mouseleave', () => {
        deleteOption.style.fontWeight = ''  // Reset the background color on mouse leave
      });

      // Create an img element for the icon
      const deleteOptionIcon = document.createElement('img');
      deleteOptionIcon.style.marginRight = '9px'
      deleteOptionIcon.style.paddingRight = '5px'
      deleteOptionIcon.src = darkMode_ ? deleteWhite : deleteIcon; //  actual path to your icon image
      deleteOptionIcon.style.width = '19px'
      // sendToBackIcon.alt = 'Send to Back';

      // Create a text label for the button
      const deleteText = document.createElement('span');
      deleteText.textContent = 'Delete';

      // Append the icon and text to the button
      deleteOption.appendChild(deleteOptionIcon);
      deleteOption.appendChild(deleteText);

      deleteOption.addEventListener('click', deleteSelected);

      //Duplicate Menu
      const duplicateOption = document.createElement('div');
      // sendToBackOption.textContent = 'Send to Back';
      duplicateOption.style.cursor = 'pointer';

      duplicateOption.addEventListener('mouseenter', () => {
        duplicateOption.style.fontWeight = '600'  // Change the background color on hover
      });

      duplicateOption.addEventListener('mouseleave', () => {
        duplicateOption.style.fontWeight = ''  // Reset the background color on mouse leave
      });

      // Create an img element for the icon
      const duplicateOptionIcon = document.createElement('img');
      duplicateOptionIcon.style.marginRight = '9px'
      duplicateOptionIcon.style.paddingRight = '5px'
      duplicateOptionIcon.src = darkMode_ ? duplicateWhite : copyIcon; //  actual path to your icon image
      duplicateOptionIcon.style.width = '20px'
      duplicateOptionIcon.style.height = '18px'

      // sendToBackIcon.alt = 'Send to Back';

      // Create a text label for the button
      const duplicateText = document.createElement('span');
      duplicateText.textContent = 'Duplicate';

      // Append the icon and text to the button
      duplicateOption.appendChild(duplicateOptionIcon);
      duplicateOption.appendChild(duplicateText);

      duplicateOption.addEventListener('click', duplicateSelection);

      //Group Menu
      const groupOption = document.createElement('div');
      // sendToBackOption.textContent = 'Send to Back';
      groupOption.style.cursor = 'pointer';

      groupOption.addEventListener('mouseenter', () => {
        groupOption.style.fontWeight = '600'  // Change the background color on hover
      });

      groupOption.addEventListener('mouseleave', () => {
        groupOption.style.fontWeight = ''  // Reset the background color on mouse leave
      });

      // Create an img element for the icon
      const groupOptionIcon = document.createElement('img');
      groupOptionIcon.style.marginRight = '8px'
      groupOptionIcon.style.paddingRight = '5px'
      groupOptionIcon.src = darkMode_ ? groupWhite : groupIcon; //  actual path to your icon image
      groupOptionIcon.style.width = '20px'
      // sendToBackIcon.alt = 'Send to Back';

      // Create a text label for the button
      const groupText = document.createElement('span');
      groupText.textContent = 'Group';

      // Append the icon and text to the button
      groupOption.appendChild(groupOptionIcon);
      groupOption.appendChild(groupText);

      groupOption.addEventListener('click', handleGroup);


      //UnGroup Menu
      const unGroupOption = document.createElement('div');
      // sendToBackOption.textContent = 'Send to Back';
      unGroupOption.style.cursor = 'pointer';

      unGroupOption.addEventListener('mouseenter', () => {
        unGroupOption.style.fontWeight = '600'  // Change the background color on hover
      });

      unGroupOption.addEventListener('mouseleave', () => {
        unGroupOption.style.fontWeight = ''  // Reset the background color on mouse leave
      });

      // Create an img element for the icon
      const unGroupOptionIcon = document.createElement('img');
      unGroupOptionIcon.style.marginRight = '8px'
      unGroupOptionIcon.style.paddingRight = '5px'
      unGroupOptionIcon.src = darkMode_ ? ungroupWhite : ungroup; //  actual path to your icon image
      unGroupOptionIcon.style.width = '20px'
      // sendToBackIcon.alt = 'Send to Back';

      // Create a text label for the button
      const unGroupText = document.createElement('span');
      unGroupText.textContent = 'Ungroup';

      // Append the icon and text to the button
      unGroupOption.appendChild(unGroupOptionIcon);
      unGroupOption.appendChild(unGroupText);

      unGroupOption.addEventListener('click', handleUngroup);

      //Present Menu
      const presentOption = document.createElement('div');
      presentOption.style.cursor = 'pointer';
      presentOption.addEventListener('mouseenter', () => {
        presentOption.style.fontWeight = '600'
      });
      presentOption.addEventListener('mouseleave', () => {
        presentOption.style.fontWeight = '400'
      });
      // Create an img element for the icon
      const presentOptionIcon = document.createElement('img');
      presentOptionIcon.style.marginRight = '12px'
      presentOptionIcon.style.marginLeft = '2px';
      presentOptionIcon.style.paddingRight = '4px'
      presentOptionIcon.src = darkMode_ ? presentModeIcon : presentModeIcon;
      presentOptionIcon.style.width = '16px'
      // Create a text label for the button
      const presentText = document.createElement('span');
      presentText.textContent = 'Present';
      // Append the icon and text to the button
      presentOption.appendChild(presentOptionIcon);
      presentOption.appendChild(presentText);
      //Attch event listner
      presentOption.addEventListener('click', enterPresentMode);

      // Append options to the context menu
      contextMenu.appendChild(bringToFrontOption);
      contextMenu.appendChild(sendToBackOption);
      contextMenu.appendChild(duplicateOption)
      contextMenu.appendChild(groupOption)
      contextMenu.appendChild(unGroupOption)
      contextMenu.appendChild(deleteOption);
      contextMenu.appendChild(presentOption);


      // Append the context menu to the document body
      document.body.appendChild(contextMenu);

      // Close the context menu when clicking outside of it
      window.addEventListener('click', function (event) {
        if (!contextMenu.contains(event.target)) {
          closeContextMenu();
        }
      });
    }
    const deleteSelected = () => {
      const activeObjects = canvasRef.current._activeObject;
      if (activeObjects) {
        if (activeObjects.type == 'group') {
          let removedObjID = activeObjects.id;
          canvasRef.current.remove(activeObjects);
          !isOffline() && emitDeleteObject(removedObjID);

        }
        if (activeObjects._objects) {
          activeObjects._objects.forEach((object) => {
            let removedObjID = object.id;
            canvasRef.current.remove(object);
            !isOffline() && emitDeleteObject(removedObjID);




          })
        } else {
          let removedObjID = activeObjects.id;
          canvasRef.current.remove(activeObjects);
          !isOffline() && emitDeleteObject(removedObjID);

        }
      }
      let canvasData = canvasRef.current.toJSON(["selectable", "id", "type"]);
      !isOffline() && emitCanvasData(DocID, canvasData)
      canvasRef.current.discardActiveObject().renderAll();
      saveStateToUndoStack()
      closeContextMenu();
    }
    canvasRef.current.on("mouse:up", function (e) {
      panning = false;
      isPanning = false;
      canvasRef.current.setCursor("default");
      canvasRef.current.renderAll();
      canvasRef.current.selection = true;
      let cvData = canvasRef.current.toJSON(["selectable", "id", "type"]);
      !isOffline() && emitCanvasData(roomID, cvData);
    });
  };

  let getFabricCanvases = (function () {
    let fabricCanvasCollection;
    return function getCanvases() {
      if (!fabricCanvasCollection) {
        fabricCanvasCollection = [];
        let fabricCanvas = document.querySelector(".canvas-container canvas");
        // console.log(fabricCanvas);
        // fabricCanvas.forEach((index, item) => {
        //     fabricCanvasCollection.push(item);
        // });
      }
      return fabricCanvasCollection;
    };
  })();

  const createGrid = (id) => {
    let grid = 50;
    //Create a grid on the screen.
    for (let i = 0; i < 4800 / grid; i++) {
      canvasRef.current.add(
        new fabric.Line([i * grid, 0, i * grid, 4800], {
          type: "line",
          stroke: "#f2f2f2",
          selectable: false,
          eraseable: false,
        })
      );
      canvasRef.current.add(
        new fabric.Line([0, i * grid, 4800, i * grid], {
          type: "line",
          stroke: "#f2f2f2",
          selectable: false,
          eraseable: false,
        })
      );
    }
    if (id) {
      let cvData = canvasRef.current.toJSON(["selectable", "id", "type"]);
      !isOffline() && emitCanvasData(id, cvData);
    }
    //Snap to grid functionality. Only enable this if
    //isSnapToGrid is set to true, otherwise print the grid but dont add the functionality

    if (isSnapToGrid) {
      canvasRef.current.on("object:moving", function (options) {
        options.target.set({
          left: Math.round(options.target.left / grid) * grid,
          top: Math.round(options.target.top / grid) * grid,
        });
      });

      canvasRef.current.on("object:modified", function (options) {
        console.log('okkkk moiii');

        let newWidth =
          Math.round((options.target.width * options.target.scaleX) / grid) *
          grid;
        let newHeight =
          Math.round((options.target.height * options.target.scaleY) / grid) *
          grid;

        options.target.set({
          width: newWidth,
          height: newHeight,
          scaleX: 1,
          scaleY: 1,
        });

        //scale objects to grid correctly
        // OnScaling(grid, canvasRef);
        saveStateToUndoStack();
      });
    }
  };
  // Zoom In
  const zoomIn = (roomID) => {
    let zoom = canvasRef.current.getZoom();
    zoom *= 0.999 ** -125;
    let zoomRatio = zoom;

    if (zoom >= 300 / 100) zoomRatio = 300 / 100;
    if (zoom <= 30 / 100) zoomRatio = 30 / 100;
    canvasRef.current.setZoom(zoomRatio);
    let cvData = canvasRef.current.toJSON(["selectable", "id", "type"]);
    !isOffline() && emitCanvasData(roomID, cvData);
    // if(canvasRef.current.getZoom().toFixed(5) > zoomMax){
    //     console.log("zoomIn: Error: cannot zoom-in anymore");
    //     return;
    // }

    // canvasRef.current.setZoom(canvasRef.current.getZoom()*SCALE_FACTOR);
    // canvasRef.current.setHeight(canvasRef.current.getHeight()*SCALE_FACTOR);
    // canvasRef.current.setWidth(canvasRef.current.getWidth()*SCALE_FACTOR);
    // canvasRef.current.renderAll();
  };

  // Zoom Out
  const zoomOut = (id) => {
    let zoom = canvasRef.current.getZoom();
    zoom *= 0.999 ** 125;
    let zoomRatio = zoom;

    if (zoom >= 300 / 100) zoomRatio = 300 / 100;
    if (zoom <= 30 / 100) zoomRatio = 30 / 100;
    canvasRef.current.setZoom(zoomRatio);
    let cvData = canvasRef.current.toJSON(["selectable", "id", "type"]);
    !isOffline() && emitCanvasData(id, cvData);
    // if( canvasRef.current.getZoom().toFixed(5) < 0.5){
    //     console.log("zoomOut: Error: cannot zoom-out anymore");
    //     return;
    // }

    // canvasRef.current.setZoom(canvasRef.current.getZoom()/SCALE_FACTOR);
    // canvasRef.current.setHeight(canvasRef.current.getHeight() / SCALE_FACTOR);
    // canvasRef.current.setWidth(canvasRef.current.getWidth() / SCALE_FACTOR);
    // canvasRef.current.renderAll();
  };

  // Reset Zoom
  const resetZoom = () => {
    canvasRef.current.setHeight(
      canvasRef.current.getHeight() / canvasRef.current.getZoom()
    );
    canvasRef.current.setWidth(
      canvasRef.current.getWidth() / canvasRef.current.getZoom()
    );
    canvasRef.current.setZoom(1);

    canvasRef.current.renderAll();

    getFabricCanvases().forEach(function (item) {
      item.css("left", 0);
      item.css("top", 0);
    });
  };

  const createImage = (roomID, imageURL, setSavingData) => {
    const imgObj = new Image();
    imgObj.src = imageURL;

    const id = uuid();

    imgObj.onload = function () {
      const image = new fabric.Image(imgObj);
      const canvasWidth = canvasRef.current.width;
      const canvasHeight = canvasRef.current.height;
      const scaleFactor = 0.5;

      // Set image properties, including left and top
      image.set({
        angle: 0,
        scaleX: (canvasWidth * scaleFactor) / image.width,
        scaleY: (canvasHeight * scaleFactor) / image.height,
        id: id,
        left: 5, // Set left position to 5
        top: 5,  // Set top position to 5
        originX: 'left',
        originY: 'top',
      });

      // Add image to canvas and render
      canvasRef.current.add(image);
      canvasRef.current.renderAll();

      // Emit image data
      !isOffline() && emitImageData({ room: roomID, id: id, image: image });

      // Convert canvas data to JSON with custom properties
      const cvData = canvasRef.current.toJSON(["selectable", "id", "type", "angle", "scaleX", "scaleY", "left", "top"]);

      // Update board data
      boardServices.updateBoardData(roomID, cvData, setSavingData);

      // Emit updated canvas data
      !isOffline() && emitCanvasData(roomID, cvData);

      // Save current state to undo stack
      saveStateToUndoStack();
    };

    imgObj.onerror = function () {
      console.error("okkFailed to load image:", imageURL);
    };
  };
  function saveStateToUndoStack() {
    let state = JSON.stringify(
      canvasRef.current.toDatalessJSON(["selectable", "id", "type"])
    );
    undoStack.push(state);
  }
  function saveStateToReloadStack() {
    let state = JSON.stringify(
      canvasRef.current.toDatalessJSON(["selectable", "id", "type"])
    );
    reloadStack.push(state);
  }

  const createRectangle = (roomID) => {
    const canvas = canvasRef.current;

    const rectOptions = {
      left: 0,
      top: 100,
      originX: "left",
      originY: "top",
      width: isMobile ? 200 : 400,
      height: isMobile ? 100 : 200,
      angle: 0,
      fill: "",
      stroke: "#000",
      strokeWidth: 1,
      selectable: true,
    };

    const maxRight = getMaxRightOfElement(canvas, "rect", "Rectangle-");
    rectOptions.left = maxRight ? maxRight + 10 : (isMobile ? 10 : 100);

    const rect = new fabric.Rect(rectOptions);
    const id = `Rectangle-${uuid()}`;
    rect.set({ id, type: "rect" });

    canvas.add(rect);
    canvas.setActiveObject(rect);
    centerObjectInViewport(canvas, rect);

    !isOffline() && emitAdd({ obj: rect, roomID, id: rect.id });
    const canvasData = canvas.toJSON(["selectable", "id", "type"]);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const getActiveObject = () => {
    alert()
    canvasRef.current.on("object:selected", onObjectSelected);

    function onObjectSelected() {
      // check if type is a property of active element
      let objType = canvasRef.current.getActiveObject().type
        ? canvasRef.current.getActiveObject().type
        : "";
      // console.log(objType);
      // your code to process the object
    }
  };

  const createSquare = (roomID) => {
    let square = new fabric.Rect({
      width: isMobile ? 80 : 100,
      height: isMobile ? 80 : 100,
      fill: "",
      stroke: "#000",
      strokeWidth: 1,
      left: isMobile ? 10 : 100,
      top: 200,
      selectable: true,
    });
    square.set({ id: uuid() });
    square.set({ type: "square" });
    canvasRef.current.add(square);
    canvasRef.current.setActiveObject(square).renderAll();
    !isOffline() && emitAdd({ obj: square, id: square.id });
    let canvasData = canvasRef.current.toJSON(["selectable", "id", "type"]);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createEllipse = (roomID) => {
    const canvas = canvasRef.current;

    const ellipseOptions = {
      rx: isMobile ? 50 : 75,
      ry: isMobile ? 35 : 50,
      fill: "",
      stroke: "#000",
      strokeWidth: 1,
      left: 0,
      top: 200,
      selectable: true,
    };

    const maxRight = getMaxRightOfElement(canvas, "ellipse", "Ellipse-");
    ellipseOptions.left = maxRight ? maxRight + 10 : (isMobile ? 100 : 200);

    const ellipse = new fabric.Ellipse(ellipseOptions);
    const id = `Ellipse-${uuid()}`;
    ellipse.set({ id, type: "ellipse" });

    canvas.add(ellipse);
    canvas.setActiveObject(ellipse);
    centerObjectInViewport(canvas, ellipse);

    !isOffline() && emitAdd({ obj: ellipse, roomID, id: ellipse.id });
    const canvasData = canvas.toJSON(["selectable", "id", "type"]);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createCircle = (roomID) => {
    const canvas = canvasRef.current;
    const circleOptions = {
      radius: isMobile ? 35 : 50,
      centeredRotation: true,
      fill: "",
      stroke: "#000",
      strokeWidth: 1,
      left: 0,
      top: 200,
      selectable: true,
    };

    const maxRight = getMaxRightOfElement(canvas, "circle", "Circle-");
    circleOptions.left = maxRight ? maxRight + 10 : 0;

    const circle = new fabric.Circle(circleOptions);
    const id = `Circle-${uuid()}`;
    circle.set({ id, type: "circle" });

    canvas.add(circle);
    canvas.setActiveObject(circle);
    centerObjectInViewport(canvas, circle);

    !isOffline() && emitAdd({ obj: circle, roomID, id: circle.id });
    let canvasData = canvas.toJSON(["selectable", "id", "type"]);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createTriangle = (roomID) => {
    const canvas = canvasRef.current;
    const triangleOptions = {
      width: isMobile ? 75 : 100,
      height: isMobile ? 110 : 200,
      fill: "",
      stroke: "#000",
      strokeWidth: 1,
      left: 200,
      top: 200,
      selectable: true,
    };

    const maxRight = getMaxRightOfElement(canvas, "triangle", "Triangle-");
    triangleOptions.left = maxRight ? maxRight + 10 : 0;

    let triangle = new fabric.Triangle(triangleOptions);
    const id = `Triangle-${uuid()}`;
    triangle.set({ id, type: "triangle" });

    canvas.add(triangle);
    canvas.setActiveObject(triangle);
    centerObjectInViewport(canvas, triangle);

    !isOffline() && emitAdd({ obj: triangle, roomID, id: triangle.id });
    let canvasData = canvas.toJSON(["selectable", "id", "type"]);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createPentagon = (roomID) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const pentagonOptions = {
      radius: isMobile ? 40 : 60,
      fill: '',
      stroke: '#000',
      strokeWidth: 1,
      left: 0,
      top: 200,
      selectable: true,
      type: 'pentagon',
    };

    const maxRight = getMaxRightOfElement(canvas, 'pentagon', 'Pentagon-');
    pentagonOptions.left = maxRight ? maxRight + 10 : (isMobile ? 100 : 200);

    const points = [];
    for (let i = 0; i < 5; i++) {
      const angle = (Math.PI / 2.5) * i - Math.PI / 2;
      points.push({
        x: pentagonOptions.radius * Math.cos(angle),
        y: pentagonOptions.radius * Math.sin(angle),
      });
    }

    const pentagon = new fabric.Pentagon({ ...pentagonOptions, points });
    const id = `Pentagon-${uuid()}`;
    pentagon.set({ id });

    canvas.add(pentagon);
    canvas.setActiveObject(pentagon);
    centerObjectInViewport(canvas, pentagon);

    !isOffline() && emitAdd({ obj: pentagon, roomID, id: pentagon.id });
    let canvasData = canvas.toJSON(["selectable", "id", "type"]);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createHexagon = (roomID) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const hexagonOptions = {
      radius: isMobile ? 40 : 60,
      fill: '',
      stroke: '#000',
      strokeWidth: 1,
      left: 0,
      top: 200,
      selectable: true,
      type: 'hexagon',
    };

    const maxRight = getMaxRightOfElement(canvas, 'hexagon', 'Hexagon-');
    hexagonOptions.left = maxRight ? maxRight + 10 : (isMobile ? 100 : 200);

    const points = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 2;
      points.push({
        x: hexagonOptions.radius * Math.cos(angle),
        y: hexagonOptions.radius * Math.sin(angle),
      });
    }

    const hexagon = new fabric.Hexagon({ ...hexagonOptions, points });

    const id = `Hexagon-${uuid()}`;
    hexagon.set({ id });

    canvas.add(hexagon);
    canvas.setActiveObject(hexagon);
    centerObjectInViewport(canvas, hexagon);

    !isOffline() && emitAdd({ obj: hexagon, roomID, id: hexagon.id });
    const canvasData = canvas.toJSON(["selectable", "id", "type"]);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createHeptagon = (roomID) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const options = {
      radius: isMobile ? 40 : 60,
      fill: '',
      stroke: '#000',
      strokeWidth: 1,
      left: 0,
      top: 200,
      selectable: true,
      type: 'heptagon',
    };

    const maxRight = getMaxRightOfElement(canvas, 'heptagon', 'Heptagon-');
    options.left = maxRight ? maxRight + 10 : (isMobile ? 100 : 200);

    const shape = new fabric.Heptagon(options);
    const id = `Heptagon-${uuid()}`;
    shape.set({ id });

    canvas.add(shape);
    canvas.setActiveObject(shape);
    centerObjectInViewport(canvas, shape);

    !isOffline() && emitAdd({ obj: shape, roomID, id });
    const canvasData = canvas.toJSON(["selectable", "id", "type"]);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createOctagon = (roomID) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const octagonOptions = {
      radius: isMobile ? 40 : 60,
      fill: '',
      stroke: '#333',
      strokeWidth: 2,
      left: 0,
      top: 200,
      selectable: true,
      type: 'octagon',
    };

    const maxRight = getMaxRightOfElement(canvas, 'octagon', 'Octagon-');
    octagonOptions.left = maxRight ? maxRight + 10 : (isMobile ? 100 : 200);

    const points = [];
    for (let i = 0; i < 8; i++) {
      const angle = (2 * Math.PI / 8) * i - Math.PI / 2;
      points.push({
        x: octagonOptions.radius * Math.cos(angle),
        y: octagonOptions.radius * Math.sin(angle),
      });
    }

    const octagon = new fabric.Octagon({ ...octagonOptions, points });
    const id = `Octagon-${uuid()}`;
    octagon.set({ id });

    canvas.add(octagon);
    canvas.setActiveObject(octagon);
    centerObjectInViewport(canvas, octagon);

    !isOffline() && emitAdd({ obj: octagon, roomID, id: octagon.id });
    let canvasData = canvas.toJSON(['selectable', 'id', 'type']);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createStar = (roomID) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const starOptions = {
      radius: isMobile ? 40 : 60,
      fill: '',
      stroke: '#000',
      strokeWidth: 1,
      left: 0,
      top: 200,
      selectable: true,
      type: 'star',
    };

    const maxRight = getMaxRightOfElement(canvas, 'star', 'Star-');
    starOptions.left = maxRight ? maxRight + 10 : (isMobile ? 100 : 200);

    const spikes = 5; // Number of star points
    const outerRadius = starOptions.radius;
    const innerRadius = outerRadius / 2;

    const points = [];
    const step = Math.PI / spikes;

    for (let i = 0; i < 2 * spikes; i++) {
      const radius = i % 2 === 0 ? outerRadius : innerRadius;
      const angle = i * step - Math.PI / 2;
      points.push({
        x: radius * Math.cos(angle),
        y: radius * Math.sin(angle),
      });
    }

    const star = new fabric.Star({ ...starOptions, points });

    const id = `Star-${uuid()}`;
    star.set({ id });

    canvas.add(star);
    canvas.setActiveObject(star);
    centerObjectInViewport(canvas, star);

    !isOffline() && emitAdd({ obj: star, roomID, id: star.id });
    const canvasData = canvas.toJSON(["selectable", "id", "type"]);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createDiamond = (roomID) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const diamondOptions = {
      radius: isMobile ? 40 : 60,
      fill: '',
      stroke: '#333',
      strokeWidth: 2,
      left: 0,
      top: 200,
      selectable: true,
      type: 'diamond',
    };

    const maxRight = getMaxRightOfElement(canvas, 'diamond', 'Diamond-');
    diamondOptions.left = maxRight ? maxRight + 10 : (isMobile ? 100 : 200);

    const size = diamondOptions.radius;
    const points = [
      { x: 0, y: -size },
      { x: size, y: 0 },
      { x: 0, y: size },
      { x: -size, y: 0 },
    ];

    const diamond = new fabric.Diamond({ ...diamondOptions, points });
    const id = `Diamond-${uuid()}`;
    diamond.set({ id });

    canvas.add(diamond);
    canvas.setActiveObject(diamond);
    centerObjectInViewport(canvas, diamond);

    !isOffline() && emitAdd({ obj: diamond, roomID, id: diamond.id });
    let canvasData = canvas.toJSON(['selectable', 'id', 'type']);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createTrapezoid = (roomID) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const trapezoidOptions = {
      radius: isMobile ? 40 : 60,
      fill: '',
      stroke: '#000',
      strokeWidth: 1,
      left: 0,
      top: 200,
      selectable: true,
      type: 'trapezoid',
    };

    const maxRight = getMaxRightOfElement(canvas, 'trapezoid', 'Trapezoid-');
    trapezoidOptions.left = maxRight ? maxRight + 10 : (isMobile ? 100 : 200);

    const size = trapezoidOptions.radius;
    const points = [
      { x: -size, y: -size / 2 },
      { x: size, y: -size / 2 },
      { x: size * 0.6, y: size / 2 },
      { x: -size * 0.6, y: size / 2 },
    ];

    const trapezoid = new fabric.Trapezoid({ ...trapezoidOptions, points });

    const id = `Trapezoid-${uuid()}`;
    trapezoid.set({ id });

    canvas.add(trapezoid);
    canvas.setActiveObject(trapezoid);
    centerObjectInViewport(canvas, trapezoid);

    !isOffline() && emitAdd({ obj: trapezoid, roomID, id: trapezoid.id });
    const canvasData = canvas.toJSON(['selectable', 'id', 'type']);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createParallelogram = (roomID) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const parallelogramOptions = {
      radius: isMobile ? 40 : 60,
      fill: '',
      stroke: '#000',
      strokeWidth: 1,
      left: 0,
      top: 200,
      selectable: true,
      type: 'parallelogram',
    };

    const maxRight = getMaxRightOfElement(canvas, 'parallelogram', 'Parallelogram-');
    parallelogramOptions.left = maxRight ? maxRight + 10 : (isMobile ? 100 : 200);

    const size = parallelogramOptions.radius;
    const points = [
      { x: -size, y: -size / 2 },
      { x: size * 0.5, y: -size / 2 },
      { x: size, y: size / 2 },
      { x: -size * 0.5, y: size / 2 },
    ];

    const parallelogram = new fabric.Parallelogram({ ...parallelogramOptions, points });

    const id = `Parallelogram-${uuid()}`;
    parallelogram.set({ id });

    canvas.add(parallelogram);
    canvas.setActiveObject(parallelogram);
    centerObjectInViewport(canvas, parallelogram);

    !isOffline() && emitAdd({ obj: parallelogram, roomID, id: parallelogram.id });
    const canvasData = canvas.toJSON(['selectable', 'id', 'type']);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createHeart = (roomID) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const heartOptions = {
      radius: isMobile ? 40 : 60,
      fill: '',
      stroke: '#000',
      strokeWidth: 1,
      left: 0,
      top: 200,
      selectable: true,
      type: 'heart',
    };

    const maxRight = getMaxRightOfElement(canvas, 'heart', 'Heart-');
    heartOptions.left = maxRight ? maxRight + 10 : (isMobile ? 100 : 200);

    const heart = new fabric.Heart({ ...heartOptions });

    const id = `Heart-${uuid()}`;
    heart.set({ id });

    canvas.add(heart);
    canvas.setActiveObject(heart);
    centerObjectInViewport(canvas, heart);

    !isOffline() && emitAdd({ obj: heart, roomID, id: heart.id });
    const canvasData = canvas.toJSON(['selectable', 'id', 'type', 'scaleX', 'scaleY']);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createCross = (roomID) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const crossOptions = {
      radius: isMobile ? 40 : 60,
      fill: '',
      stroke: '#000',
      strokeWidth: 1,
      left: 0,
      top: 200,
      selectable: true,
      type: 'cross',
    };

    const maxRight = getMaxRightOfElement(canvas, 'cross', 'Cross-');
    crossOptions.left = maxRight ? maxRight + 10 : (isMobile ? 100 : 200);

    const size = crossOptions.radius;
    const arm = size * 0.3;
    const points = [
      { x: -arm, y: -size },
      { x: arm, y: -size },
      { x: arm, y: -arm },
      { x: size, y: -arm },
      { x: size, y: arm },
      { x: arm, y: arm },
      { x: arm, y: size },
      { x: -arm, y: size },
      { x: -arm, y: arm },
      { x: -size, y: arm },
      { x: -size, y: -arm },
      { x: -arm, y: -arm },
    ];

    const cross = new fabric.Cross({ ...crossOptions, points });

    const id = `Cross-${uuid()}`;
    cross.set({ id });

    canvas.add(cross);
    canvas.setActiveObject(cross);
    centerObjectInViewport(canvas, cross);

    !isOffline() && emitAdd({ obj: cross, roomID, id: cross.id });
    const canvasData = canvas.toJSON(['selectable', 'id', 'type']);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createChevron = (roomID) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const chevronOptions = {
      radius: isMobile ? 40 : 60,
      fill: '',
      stroke: '#000',
      strokeWidth: 1,
      left: 0,
      top: 200,
      selectable: true,
      type: 'chevron',
    };

    const maxRight = getMaxRightOfElement(canvas, 'chevron', 'Chevron-');
    chevronOptions.left = maxRight ? maxRight + 10 : (isMobile ? 100 : 200);

    const size = chevronOptions.radius;
    const points = [
      { x: -size, y: -size * 0.5 },
      { x: 0, y: -size * 0.5 },
      { x: size, y: 0 },
      { x: 0, y: size * 0.5 },
      { x: -size * 0.5, y: size * 0.5 },
      { x: -size, y: size * 0.25 },
      { x: -size * 0.5, y: 0 },
    ];

    const chevron = new fabric.Chevron({ ...chevronOptions, points });

    const id = `Chevron-${uuid()}`;
    chevron.set({ id });

    canvas.add(chevron);
    canvas.setActiveObject(chevron);
    centerObjectInViewport(canvas, chevron);

    !isOffline() && emitAdd({ obj: chevron, roomID, id: chevron.id });
    const canvasData = canvas.toJSON(['selectable', 'id', 'type']);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createSemicircle = (roomID) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const semicircleOptions = {
      radius: isMobile ? 40 : 60,
      fill: '',
      stroke: '#000',
      strokeWidth: 1,
      left: 0,
      top: 200,
      selectable: true,
      type: 'semicircle',
    };

    const maxRight = getMaxRightOfElement(canvas, 'semicircle', 'Semicircle-');
    semicircleOptions.left = maxRight ? maxRight + 10 : (isMobile ? 100 : 200);

    const semicircle = new fabric.Semicircle({ ...semicircleOptions });

    const id = `Semicircle-${uuid()}`;
    semicircle.set({ id });

    canvas.add(semicircle);
    canvas.setActiveObject(semicircle);
    centerObjectInViewport(canvas, semicircle);

    !isOffline() && emitAdd({ obj: semicircle, roomID, id: semicircle.id });
    const canvasData = canvas.toJSON(['selectable', 'id', 'type']);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createPentagram = (roomID) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const pentagramOptions = {
      radius: isMobile ? 40 : 50,
      fill: '#fff',
      stroke: '#333',
      strokeWidth: 2,
      left: 0,
      top: 200,
      selectable: true,
      type: 'pentagram',
    };

    const maxRight = getMaxRightOfElement(canvas, 'pentagram', 'Pentagram-');
    pentagramOptions.left = maxRight ? maxRight + 10 : (isMobile ? 100 : 200);

    const basePoints = calculatePolygonPoints(5, pentagramOptions.radius);
    const order = [0, 2, 4, 1, 3];
    pentagramOptions.points = order.map(i => basePoints[i]);

    const pentagram = new fabric.Pentagram({ ...pentagramOptions });

    const id = `Pentagram-${uuid()}`;
    pentagram.set({ id });

    canvas.add(pentagram);
    canvas.setActiveObject(pentagram);
    centerObjectInViewport(canvas, pentagram);

    !isOffline() && emitAdd({ obj: pentagram, roomID, id: pentagram.id });
    const canvasData = canvas.toJSON(['selectable', 'id', 'type']);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createCrescent = (roomID) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const crescentOptions = {
      radius: isMobile ? 40 : 60,
      fill: '',
      stroke: '#000',
      strokeWidth: 1,
      left: 0,
      top: 200,
      selectable: true,
      type: 'crescent',
    };

    const maxRight = getMaxRightOfElement(canvas, 'crescent', 'Crescent-');
    crescentOptions.left = maxRight ? maxRight + 10 : (isMobile ? 100 : 200);

    const crescent = new fabric.Crescent({ ...crescentOptions });

    const id = `Crescent-${uuid()}`;
    crescent.set({ id });

    canvas.add(crescent);
    canvas.setActiveObject(crescent);
    centerObjectInViewport(canvas, crescent);

    !isOffline() && emitAdd({ obj: crescent, roomID, id: crescent.id });
    const canvasData = canvas.toJSON(['selectable', 'id', 'type']);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };


  const createRightAngleTriangle = (roomID) => {
    const points = [
      { x: 200, y: 200 }, // Top-left point
      { x: 200, y: 300 }, // Bottom-left point
      { x: 300, y: 300 }, // Bottom-right point
    ];

    const rightAngleTriangle = new fabric.Polygon(points, {
      fill: "",
      stroke: "#000",
      strokeWidth: 1,
      selectable: true,
    });
    rightAngleTriangle.set({ id: uuid() });
    rightAngleTriangle.set({ type: "right-angle-triangle" });
    canvasRef.current.add(rightAngleTriangle);
    canvasRef.current.setActiveObject(rightAngleTriangle).renderAll();
    !isOffline() && emitAdd({ obj: rightAngleTriangle, id: rightAngleTriangle.id });
    let canvasData = canvasRef.current.toJSON(["selectable", "id", "type"]);
   !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };
  const createCylinder = (roomID) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const radius = isMobile ? 40 : 60;
    const leftOffset = isMobile ? 100 : 200;
    const topOffset = 200;

    // Find next available horizontal position
    const maxRight = getMaxRightOfElement(canvas, 'cylinder', 'Cylinder-');
    const left = maxRight ? maxRight + 10 : leftOffset;

    // Create cylinder using custom fabric.Cylinder class
    const cylinder = new fabric.Cylinder({
      left,
      top: topOffset,
      radius,
      fill: 'transparent',
      stroke: '#333',
      strokeWidth: 2,
      selectable: true,
      id: `Cylinder-${uuid()}`,
    });

    // Add to canvas and center in viewport
    canvas.add(cylinder);
    canvas.setActiveObject(cylinder);
    centerObjectInViewport(canvas, cylinder);

    // Emit changes
    !isOffline() && emitAdd({ obj: cylinder, roomID, id: cylinder.id });
    const canvasData = canvas.toJSON(['selectable', 'id', 'type', 'radius']);
    !isOffline() && emitCanvasData(roomID, canvasData);

    // Save to undo stack
    saveStateToUndoStack();
  };


  const createRightArrow = (roomID) => {
    const arrowPath = "M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z";
    // const linePath = "M 0 15 L -40 15";

    // const combinedPath = `${arrowPath} ${linePath}`;

    const arrow = new fabric.Path(arrowPath, {
      left: 100,
      top: 100,
      fill: "",
      stroke: "#000",
      strokeWidth: 1,
      selectable: true,
    });
    arrow.set({ id: uuid() });
    arrow.set({ type: "arrow" });
    canvasRef.current.add(arrow);
    canvasRef.current.setActiveObject(arrow).renderAll();
    !isOffline() && emitAdd({ obj: arrow, id: arrow.id });
    let canvasData = canvasRef.current.toJSON(["selectable", "id", "type"]);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createIText = (roomID, obj) => {
    let textOptions = {
      left: isMobile ? 100 : 200,
      top: 200,
      fill: "#000",
    };
    let textObject = new fabric.IText("Enter text here", textOptions);
    textObject.set({ id: uuid() });
    textObject.set({ type: "i-text" });
    canvasRef.current.add(textObject);
    canvasRef.current.renderAll();
    obj.setTextAlign(textOptions.textAlign);
    obj.setTextColor(textOptions.fill);
    obj.setTextFont(textOptions.fontFamily);
    obj.setTextSize(textOptions.fontSize);
    obj.setTextStyle(textOptions.fontStyle);
    !isOffline() && emitText({ room: DocID, obj: textObject, id: textObject.id });
    let canvasData = canvasRef.current.toJSON(["selectable", "id", "type"]);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const disableSelection = (selectionModeT) => {
    if (selectionModeT) {
      canvasRef.current.selection = selectionModeT;
      canvasRef.current.forEachObject(function (o) {
        if (o.type !== "line") {
          o.selectable = selectionModeT;
        }
      });
    } else {
      canvasRef.current.selection = selectionModeT;
      canvasRef.current.forEachObject(function (o) {
        if (o.type !== "line") {
          o.selectable = selectionModeT;
        }
      });
    }
    canvasRef.current.renderAll();
  };
  const Cylinder = fabric.util.createClass(fabric.Object, {
    type: 'cylinder',

    initialize(options) {
      options || (options = {});

      this.callSuper('initialize', options);
      this.set('radius', options.radius || 0);
      this.set('height', options.height || 0);
      this.set('fill', options.fill || 'transparent');
      this.set('stroke', options.stroke || '#000');
      this.set('strokeWidth', options.strokeWidth || 1);
      this.set('selectable', options.selectable !== undefined ? options.selectable : true);
    },

    toObject() {
      return fabric.util.object.extend(this.callSuper('toObject'), {
        radius: this.get('radius'),
        height: this.get('height'),
        fill: this.get('fill'),
        stroke: this.get('stroke'),
        strokeWidth: this.get('strokeWidth'),
        selectable: this.get('selectable'),
      });
    },

    _render(ctx) {
      // Draw cylinder using canvas methods
      const radius = this.get('radius');
      const height = this.get('height');

      ctx.beginPath();
      ctx.moveTo(radius, 0);
      ctx.lineTo(radius, height);
      ctx.arc(0, height, radius, Math.PI, 0, true);
      ctx.lineTo(-radius, 0);
      ctx.arc(0, 0, radius, 0, Math.PI, true);
      ctx.closePath();

      this.fillAndStroke(ctx); // Fill and stroke the shape
    },
  });

  fabric.Cylinder = Cylinder;

  fabric.Cylinder = Cylinder;
  const enableSelection = () => {
    canvasRef.current.selection = true;
    canvasRef.current.renderAll();
  };

  const convertCanvasToImage = (event) => {

    let link = event.currentTarget;

    //creating URL of canvas image

    let fullImage = canvasRef.current.toDataURL("image/png").replace("image/png", "image/octet-stream");

    //setting the link of anchor tag to the link that we create above

    link.setAttribute('href', fullImage);

    //setting default name of image to be downloaded

    link.download = "canvas-image.png";
  };

  //Function Below changes the BG color of canvas and sets the color as passed to it from Colorpicker @ Title.js

  const recolorBG = (roomID, newColor) => {
    let canvaBG = canvasRef.current;
    if (canvaBG) {
      canvasRef.current.set('backgroundColor', newColor);

      //Whole canvas will render after getting colored
      canvasRef.current.renderAll();

      // Emit the background color change to the server
      emitBackgroundColorChange(roomID, newColor);

      //deselecting activeobjects after recoloring shapes     
      canvasRef.current.discardActiveObject();

      let canvasData = canvasRef.current.toJSON(["selectable", "id", "type"]);
      !isOffline() && emitCanvasData(roomID, canvasData);

      //State of canvas with Color will saved into Undo State
      saveStateToUndoStack();


    };


  }


  // Function below Recolors the Objects, takes the color arguement and sets the property of object to the color passed

  const recolorObjects = (newColor) => {

    let activeObjects = canvasRef.current.getActiveObjects();
    // console.log("Before Color getting Active ", activeObjects);      //Commented Code : Getting the selected objects at console

    if (activeObjects) {

      //activeObjects return multiple objects thus for each object to be colored we need to recolor all objects thus using foreach loop
      activeObjects.forEach(function (object) {
        if (object.type == 'group') {
          object._objects.map((item) => { if (item.type !== 'i-text') { item.set('fill', newColor) } });
        }
        // console.log("object", object);
        object.set('fill', newColor);
        // object.set('stroke', newColor); // You can set the stroke color if needed
        !isOffline() && emitShapeColorChange(DocID, newColor, object.id)
        let canvasData = canvasRef.current.toJSON(["selectable", "id", "type"]);
        !isOffline() && emitCanvasData(DocID, canvasData);
      });


      canvasRef.current.renderAll();
      // console.log(canvasRef.current); // Commented Code : logged on console the selected object
      // canvasRef.current.discardActiveObject();

    }
    //State of Shapes of canvas with Color will saved into Undo State
    saveStateToUndoStack();

  };

  //Function below Changes Stroke/Border of selected Objects/Items
  const recolorStroke = (newColor) => {

    let activeObjects = canvasRef.current.getActiveObjects();
    // console.log("Before Color getting Active ", activeObjects);      //Commented Code : Getting the selected objects at console

    if (activeObjects) {

      //activeObjects return multiple objects thus for each object to be colored we need to recolor all objects thus using foreach loop
      activeObjects.forEach(function (object) {
        if (object.type == 'group') {
          object._objects.map((item) => { if (item.type !== 'i-text') { item.set('stroke', newColor) } });
        }

        object.set('stroke', newColor); // You can set the stroke color if needed
        !isOffline() && emitStrokeColorChange(DocID, newColor, object.id)
        let canvasData = canvasRef.current.toJSON(["selectable", "id", "type"]);
        !isOffline() && emitCanvasData(DocID, canvasData);
      });


      canvasRef.current.renderAll();
      // console.log(canvasRef.current); // Commented Code : logged on console the selected object
      // canvasRef.current.discardActiveObject();

    }
    //State of Shapes of canvas with Color will saved into Undo State
    saveStateToUndoStack();

  };

  //    Function Below returns the active Object on Canvas and is called at Title.JS  

  function setColorpickerVisiblity() {
    if (canvasRef.current) {
      return canvasRef.current._activeObject;
    }
  }

  /**
 * Function Below:
  * Duplicates the currently selected objects on the canvas and adds the duplicates slightly offset.
  * This function retrieves the active objects on the canvas and creates clones of each object,
  * positioning the clones with a small offset from their original positions. The duplicates are then
  * added to the canvas, and the canvas state is updated accordingly.
  * 
  * @remarks
  * The function also triggers the saving of the current canvas state to the undo stack to allow for
  * subsequent undo operations.
  * 
  * @param {void}
  * @returns {void}
  */
  const duplicateSelection = () => {
    // Retrieve the currently active objects on the canvas
    const activeObjects = canvasRef.current.getActiveObjects();

    if (activeObjects) {
      // Iterate through each active object
      activeObjects.forEach(function (object) {
        // console.log("From Duplicate", object);

        // Clone the object and add the clone to the canvas with an offset
        object.clone(function (clone) {
          canvasRef.current.add(clone.set({
            id: uuid(),
            left: object.left + 20,
            top: object.top + 20,
            fill: object.fill
          }));
          if (clone.type !== 'image') {
            !isOffline() && emitClonedObj(clone.id, clone, DocID)
          } else {
            !isOffline() && emitImageData({ room: DocID, id: clone.id, image: clone })
          }
        });
        // let objectInfo={object.id}
        // Emiting to show copy the duplication in other clients
        console.log("Object From Context", object)



      });
    }

    // Save the current canvas state to the undo stack
    saveStateToUndoStack();

    // Render the updated canvas state
    canvasRef.current.renderAll();

    //   let canvasData = canvasRef.current.toJSON(["selectable", "id", "type"]);
    // emitCanvasData(DocID, canvasData);

    // Discard the active object selection
    canvasRef.current.discardActiveObject();
  };
  /**
   * Clears the canvas by removing all objects from it.
   */
  const clearCanvas = () => {
    // Use the canvasRef to access the current canvas instance.
    // Call the remove method on the canvas instance to remove all objects.
    canvasRef.current.set('backgroundColor', '');

    let clearedCanvas = canvasRef.current.remove(...canvasRef.current.getObjects());
    //Emitter to clear canvas sent to server here
    !isOffline() && emitClearCanvas({ DocID, clearedCanvas });
    //Saving the Data of Canvas into File 
    let canvasData = canvasRef.current.toJSON(["selectable", "id", "type"]);
    !isOffline() && emitCanvasData(DocID, canvasData);

    //Saving state in Undo Stack so on Undo it retrieves from Stack
    saveStateToUndoStack();

  };


  const handleUngroup = () => {
    if (!canvasRef.current.getActiveObject()) {
      return;
    }
    if (canvasRef.current.getActiveObject().type !== "group") {
      return;
    }
    let activeObjects = canvasRef.current
    let groupId = canvasRef.current.getActiveObject().id
    if (activeObjects._activeObjects) {
      // groupId=activeObjects._activeObjects.id
    }
    // emitObjID(objID)
    canvasRef.current.getActiveObject().toActiveSelection();
    // console.log('groupId',groupId)
    !isOffline() && emitUnGroup({ groupId })
    canvasRef.current.requestRenderAll();
  };


  const handleGroup = () => {
    if (!canvasRef.current.getActiveObject()) {
      return;
    }
    if (canvasRef.current.getActiveObject().type !== "activeSelection") {
      return;
    }
    let objIDArr = []
    let objArr = [...canvasRef.current.getActiveObjects()];
    canvasRef.current.getActiveObjects().forEach((obj) => {
      objIDArr.push(obj.id)
    })
    //let objIDArr = [];
    console.log('objIDArr :', objIDArr + '/n objArray :', objArr)
    let groupId = uuid()
    canvasRef.current.getActiveObject().toGroup().set({ id: groupId });
    !isOffline() && emitObjID({ groupId, objIDArr, objArr });

    canvasRef.current.requestRenderAll();
  };
  let darkMode_;

  const setMode = (darkMode) => {
    darkMode_ = darkMode

    // console.log('darkMode : ',darkMode)
    // alert('Dark Mode')
    if (darkMode) {
      enableDarkMode({
        brightness: 100,
        contrast: 90,
        sepia: 10,
      });
    } else {
      disableDarkMode();

    }
  }
  // Function to Detect any activity to set var saving... and Saved
  const savingLastChanges = (change) => {
    lastChangeRef.current = change;
  };

  const createFrame = (roomID, options = {}) => {
    const canvas = canvasRef.current;
    const frameOptions = {
      rx: options.borderRadius || 10,
      ry: options.borderRadius || 10,
      width: options.width || 100,
      height: options.height || 100,
      left: 0,
      top: 0,
      ...options,
    };

    // Calculate position for the new frame
    const maxRight = getMaxRightOfElement(canvas, "rect", "Frame-");
    // Place the new frame to the right of the rightmost frame with a 10px gap
    frameOptions.left = maxRight ? maxRight + 10 : 0;

    const frame = new fabric.Rect(frameOptions);
    const id = `Frame-${uuid()}`;
    frame.id = id;
    frame.name = id;

    canvas.add(frame);
    canvas.setActiveObject(frame);
    centerObjectInViewport(canvas, frame);

    !isOffline() && emitAdd({ obj: frame, roomID, id: frame.id });
    let canvasData = canvasRef.current.toJSON(["selectable", "id", "type"]);
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const createStickyNote = (roomID, config) => {
    const canvas = canvasRef.current;
    const {
      width = 200,
      height = 150,
      backgroundColor = '#FFEB3B',
      left = 100,
      top = 100,
      fontSize = 16,
      text = '',
      textColor = '#333', // Actual text color
    } = config || {};

    // Create the rectangle for the sticky note background
    const rect = new fabric.Rect({
      width,
      height,
      left,
      top,
      fill: backgroundColor,
      rx: 10,
      ry: 10,
      selectable: true,
      hasBorders: true,
      lockScalingFlip: true,
      shadow: {
        color: 'rgba(0,0,0,0.3)',
        blur: 10,
        offsetX: 5,
        offsetY: 5,
      },
    });

    // Create the textbox for the sticky note text without placeholder
    const noteText = new fabric.Textbox(text, {
      width: width - 20, // Slightly smaller than the rectangle width
      left: left + 10, // Padding inside the rectangle
      top: top + 10,
      fontSize,
      fill: textColor, // Text color
      textAlign: 'left',
      editable: true, // Make the text box editable
      lockScalingFlip: true,
      padding: 10, // Inner padding for text
      selectable: true,
    });
    // Create a group to combine the rectangle and text box
    const stickyNote = new fabric.Group([rect, noteText], {
      left,
      top,
      lockScalingFlip: true,
      hasBorders: true,
      hasControls: true,
      selectable: true
    });
    const id = `sticky-note-${uuid()}`;
    stickyNote.id = id;
    // Add the sticky note to the canvas
    canvas.add(stickyNote);
    canvas.setActiveObject(stickyNote);
    canvas.renderAll();
    // Save and emit canvas data (include editable state)
    const canvasData = canvas.toJSON(['selectable', 'id', 'type']);
    !isOffline() && emitAdd({ obj: stickyNote, roomID, id: stickyNote.id });
    !isOffline() && emitCanvasData(roomID, canvasData);
    saveStateToUndoStack();
  };

  const enterPresentMode = () => {
    const canvas = canvasRef.current;
    const selectedObject = canvas.getActiveObject();

    if (!selectedObject) {
      alert("Please select an object to present.");
      return;
    }

    const fullscreenContainer = document.createElement("div");
    fullscreenContainer.style.position = "fixed";
    fullscreenContainer.style.top = "0";
    fullscreenContainer.style.left = "0";
    fullscreenContainer.style.width = "100vw";
    fullscreenContainer.style.height = "100vh";
    fullscreenContainer.style.display = "flex";
    fullscreenContainer.style.alignItems = "center";
    fullscreenContainer.style.justifyContent = "center";
    fullscreenContainer.style.backgroundColor = "white";
    document.body.appendChild(fullscreenContainer);

    selectedObject.clone((cloned) => {
      const dataURL = cloned.toDataURL();
      let img = document.createElement("img");
      img.src = dataURL;
      img.style.maxWidth = "90vw";
      img.style.maxHeight = "90vh";
      fullscreenContainer.appendChild(img);
    });

    if (fullscreenContainer.requestFullscreen) {
      fullscreenContainer.requestFullscreen();
    } else if (fullscreenContainer.webkitRequestFullscreen) {
      fullscreenContainer.webkitRequestFullscreen();
    } else if (fullscreenContainer.msRequestFullscreen) {
      fullscreenContainer.msRequestFullscreen();
    }

    document.addEventListener("fullscreenchange", () => {
      if (!document.fullscreenElement) {
        fullscreenContainer.remove();
      }
    });
  };

  return (
    <CanvasContext.Provider
      value={{
        savingLastChanges,
        lastChange: () => lastChangeRef.current, // Function to retrieve the value
        darkMode_,
        setMode,
        recolorStroke,
        handleGroup,
        handleUngroup,
        saveStateToReloadStack,
        reloadStack,
        DocID,
        clearCanvas,
        saveStateToUndoStack,
        duplicateSelection,
        setColorpickerVisiblity,
        recolorObjects,
        recolorBG,
        canvasRef,
        prepareCanvas,
        createRectangle,
        createTriangle,
        createCircle,
        createIText,
        createGrid,
        setSnapToGrid,
        isSnapToGrid,
        Panning,
        zoomIn,
        zoomOut,
        resetZoom,
        createImage,
        getActiveObject,
        disableSelection,
        enableSelection,
        convertCanvasToImage,
        undoStack,
        redoStack,
        createSquare,
        createEllipse,
        createCylinder,
        createRightAngleTriangle,
        createDiamond,
        createRightArrow,
        setIsChangeSomething,
        isChangeSomething,
        createFrame,
        createStickyNote,
        createPentagon,
        createHexagon,
        createHeptagon,
        createOctagon,
        createStar,
        createTrapezoid,
        createParallelogram,
        createHeart,
        createCross,
        createChevron,
        createSemicircle,
        createPentagram,
        createCrescent,
      }}
    >
      {children}
    </CanvasContext.Provider>
  );
};

export const useCanvas = () => useContext(CanvasContext);
